import { useTranslation } from 'react-i18next';

// AuditChangesTable renders the expanded-row field/before/after table for an
// audit entry whose metadata carries a `changes` array. Two row shapes exist:
//   - generic field diffs: {field, old, new} (+ redacted: true when the value
//     must never be shown, e.g. personnummer)
//   - attendance_confirm rows: {member_id, activity_id, member_name,
//     activity_title, activity_starts_at, old, new} resolved server-side.
// Values arrive as strings; "true"/"false" localize to yes/no and RFC3339
// timestamps format through the caller's locale-aware 24h formatter.

const META_SEPARATOR = ' · ';

// Matches RFC3339 instants (what audit.ChangeSet.Timestamp stores). Date-only
// values (YYYY-MM-DD) intentionally do NOT match — ISO dates render as-is.
const ISO_TIMESTAMP = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

// metaStr safely coerces an unknown metadata value to a string ('' otherwise)
// so change objects can be read without per-field type guards.
function metaStr(v: unknown): string {
  return typeof v === 'string' ? v : '';
}

type TFn = ReturnType<typeof useTranslation>['t'];

// attendanceStatusLabel localizes an attendance status code for the change
// accordion. An empty code means the cell was unmarked / cleared.
function attendanceStatusLabel(t: TFn, code: string): string {
  if (!code) return t('settings.activity.changes.attendanceStatus.none');
  return t(`settings.activity.changes.attendanceStatus.${code}`, { defaultValue: code });
}

// changeValueLabel renders one before/after cell value: empty → em dash,
// booleans → localized yes/no, RFC3339 timestamps → 24h locale format,
// everything else verbatim.
function changeValueLabel(t: TFn, dateFmt: Intl.DateTimeFormat, v: string): string {
  if (!v) return '—';
  if (v === 'true') return t('settings.activity.changes.values.true');
  if (v === 'false') return t('settings.activity.changes.values.false');
  if (ISO_TIMESTAMP.test(v)) {
    const d = new Date(v);
    if (!Number.isNaN(d.getTime())) return dateFmt.format(d);
  }
  return v;
}

export function AuditChangesTable({
  changes,
  action,
  dateFmt,
}: {
  changes: Array<Record<string, unknown>>;
  action: string;
  dateFmt: Intl.DateTimeFormat;
}) {
  const { t } = useTranslation('common');
  const isAttendanceChanges = action === 'attendance_confirm';

  return (
    <table className="w-full text-xs">
      <thead>
        <tr className="border-b border-border">
          <th className="w-48 px-6 py-2 text-left font-heading font-semibold text-muted-foreground">
            {isAttendanceChanges
              ? t('settings.activity.changes.who')
              : t('settings.activity.changes.field')}
          </th>
          <th className="px-3 py-2 text-left font-heading font-semibold text-muted-foreground">
            {t('settings.activity.changes.before')}
          </th>
          <th className="px-3 py-2 text-left font-heading font-semibold text-muted-foreground">
            {t('settings.activity.changes.after')}
          </th>
        </tr>
      </thead>
      <tbody>
        {changes.map((c, idx) => {
          const redacted = c.redacted === true;
          const before = isAttendanceChanges
            ? attendanceStatusLabel(t, metaStr(c.old))
            : changeValueLabel(t, dateFmt, metaStr(c.old));
          const after = isAttendanceChanges
            ? attendanceStatusLabel(t, metaStr(c.new))
            : changeValueLabel(t, dateFmt, metaStr(c.new));
          const label = isAttendanceChanges
            ? metaStr(c.member_name) || t('settings.activity.changes.removedMember')
            : t(`settings.activity.changes.fields.${metaStr(c.field)}`, {
                defaultValue: metaStr(c.field),
              });
          const context =
            isAttendanceChanges && metaStr(c.activity_title)
              ? metaStr(c.activity_starts_at)
                ? `${metaStr(c.activity_title)}${META_SEPARATOR}${dateFmt.format(new Date(metaStr(c.activity_starts_at)))}`
                : metaStr(c.activity_title)
              : '';
          return (
            <tr
              key={metaStr(c.field) || `${metaStr(c.member_id)}-${metaStr(c.activity_id)}-${idx}`}
              className="border-b border-border/40 last:border-0"
            >
              <td className="px-6 py-2 align-top">
                <span className="font-heading font-semibold text-foreground">{label}</span>
                {context && (
                  <span className="block font-normal text-muted-foreground/80">{context}</span>
                )}
              </td>
              {redacted ? (
                <td colSpan={2} className="px-3 py-2 italic text-muted-foreground">
                  {t('settings.activity.changes.redactedValue')}
                </td>
              ) : (
                <>
                  <td className="max-w-[14rem] truncate px-3 py-2 text-muted-foreground line-through decoration-muted-foreground/40">
                    {before}
                  </td>
                  <td className="max-w-[14rem] truncate px-3 py-2 text-foreground">
                    {after}
                  </td>
                </>
              )}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
